import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, subQuarters, startOfYear, endOfYear, subYears } from 'date-fns';
import { Download, ChevronDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const fmtCurrency = v => `$${parseFloat(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = v => v ? v.slice(0, 10) : '—';

const PERIODS = [
  { label: 'This month',    value: 'this_month' },
  { label: 'Last month',    value: 'last_month' },
  { label: 'This quarter',  value: 'this_quarter' },
  { label: 'Last quarter',  value: 'last_quarter' },
  { label: 'This year',     value: 'this_year' },
  { label: 'Last year',     value: 'last_year' },
];

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

function getPeriodDates(period) {
  const now = new Date();
  switch (period) {
    case 'last_month':   return { sd: format(startOfMonth(subMonths(now, 1)), 'yyyy-MM-dd'), ed: format(endOfMonth(subMonths(now, 1)), 'yyyy-MM-dd') };
    case 'this_quarter': return { sd: format(startOfQuarter(now), 'yyyy-MM-dd'), ed: format(endOfQuarter(now), 'yyyy-MM-dd') };
    case 'last_quarter': return { sd: format(startOfQuarter(subQuarters(now, 1)), 'yyyy-MM-dd'), ed: format(endOfQuarter(subQuarters(now, 1)), 'yyyy-MM-dd') };
    case 'this_year':    return { sd: format(startOfYear(now), 'yyyy-MM-dd'), ed: format(endOfYear(now), 'yyyy-MM-dd') };
    case 'last_year':    return { sd: format(startOfYear(subYears(now, 1)), 'yyyy-MM-dd'), ed: format(endOfYear(subYears(now, 1)), 'yyyy-MM-dd') };
    default:             return { sd: format(startOfMonth(now), 'yyyy-MM-dd'), ed: format(endOfMonth(now), 'yyyy-MM-dd') };
  }
}

function getPeriodLabel(period, sd, ed) {
  const label = PERIODS.find(p => p.value === period)?.label || 'This month';
  const fmt = d => { try { return format(new Date(d + 'T00:00:00'), 'd MMM yyyy'); } catch { return d; } };
  return `${label}: ${fmt(sd)} - ${fmt(ed)}`;
}

export default function FundBalances() {
  const [period, setPeriod]   = useState('this_month');
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('all');
  const [page, setPage]       = useState(1);
  const [limit, setLimit]     = useState(50);
  const [data, setData]       = useState({ rows: [], total: 0, page: 1, limit: 50 });
  const [loading, setLoading] = useState(false);

  const { sd, ed } = getPeriodDates(period);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: sd, end_date: ed, search, status, page, limit });
    api.get(`/reports/fund-balances?${p}`)
      .then(r => setData(r.data))
      .catch(() => { toast.error('Failed to load fund balances'); setData({ rows: [], total: 0, page: 1, limit }); })
      .finally(() => setLoading(false));
  }, [sd, ed, search, status, page, limit]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [period, search, status, limit]);

  function downloadCSV() {
    if (!data?.rows?.length) return;
    const cols = ['Client', 'Fund', 'Client Type', 'Client Status', 'Start Date', 'Expires', 'Amount', 'Starting Balance', 'Balance', '% Depletion'];
    const rows = data.rows.map(r => [r.client_name, r.fund, r.client_type, r.client_status, fmtDate(r.start_date), fmtDate(r.expires), r.amount, r.starting_balance, r.balance, r.pct_depletion]);
    const csv  = [cols, ...rows].map(r => r.join(',')).join('\n');
    const a    = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `fund_balances_${sd}_${ed}.csv`;
    a.click();
  }

  const totalPages = Math.max(1, Math.ceil(data.total / limit));
  const from = data.total === 0 ? 0 : (page - 1) * limit + 1;
  const to   = Math.min(page * limit, data.total);

  return (
    <div className="p-0">
      {/* Top controls bar */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200 flex-wrap">
        {/* Period dropdown */}
        <div className="relative">
          <select value={period} onChange={e => setPeriod(e.target.value)}
            className="appearance-none border border-slate-300 rounded px-3 py-1.5 pr-8 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer">
            {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
        </div>

        {/* Period label */}
        <span className="text-sm text-slate-600 font-medium">{getPeriodLabel(period, sd, ed)}</span>

        <div className="flex-1"/>

        {/* Search */}
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input
            type="text" placeholder="Search client..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="border border-slate-300 rounded pl-8 pr-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>

        {/* Status filter */}
        <div className="relative">
          <select value={status} onChange={e => setStatus(e.target.value)}
            className="appearance-none border border-slate-300 rounded px-3 py-1.5 pr-8 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
        </div>

        {/* Download */}
        <button onClick={downloadCSV} disabled={!data?.rows?.length} title="Download CSV"
          className="p-1.5 border border-slate-300 rounded text-slate-500 hover:bg-slate-50 disabled:opacity-40">
          <Download size={15}/>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-white">
              {['Client','Fund','Client Type','Client Status','Start Date','Expires','Amount','Starting Balance','Balance','% Depletion'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap border-b border-slate-200">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400 text-sm">Loading…</td></tr>
            ) : !data?.rows?.length ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-slate-400 text-sm">
                  No records to show for this date range
                </td>
              </tr>
            ) : (
              data.rows.map((row, i) => {
                const pct = parseFloat(row.pct_depletion || 0);
                const bal = parseFloat(row.balance || 0);
                return (
                  <tr key={row.id || i} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-2.5 font-medium text-slate-800 whitespace-nowrap">{row.client_name}</td>
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{row.fund || '—'}</td>
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{row.client_type || '—'}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        row.client_status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                      }`}>{row.client_status}</span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(row.start_date)}</td>
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(row.expires)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600 whitespace-nowrap">{fmtCurrency(row.amount)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600 whitespace-nowrap">{fmtCurrency(row.starting_balance)}</td>
                    <td className={`px-4 py-2.5 text-right font-medium whitespace-nowrap ${bal < 0 ? 'text-red-600' : 'text-slate-700'}`}>
                      {fmtCurrency(row.balance)}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-slate-100 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full ${pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ width: `${Math.min(100, pct)}%` }}/>
                        </div>
                        <span className="text-xs text-slate-600 w-10 text-right">{pct.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-slate-200 text-sm text-slate-600 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span>Showing {from} - {to} of {data.total} clients</span>
          <select value={limit} onChange={e => setLimit(Number(e.target.value))}
            className="border border-slate-300 rounded px-2 py-1 text-sm ml-3 focus:outline-none">
            {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>clients per page</span>
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1}
            className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40">
            <ChevronLeft size={14}/>
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pg = totalPages <= 5 ? i + 1 : page <= 3 ? i + 1 : page + i - 2;
            if (pg > totalPages) return null;
            return (
              <button key={pg} onClick={() => setPage(pg)}
                className={`w-7 h-7 rounded text-xs font-medium border ${pg === page ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                {pg}
              </button>
            );
          })}
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages}
            className="p-1.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40">
            <ChevronRight size={14}/>
          </button>
        </div>

      </div>
    </div>
  );
}
